import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
    FC,
    memo,
    useCallback,
    useEffect,
    useState,
    useRef,
} from "react";
import OutsideClick from "../../outside-click/outside-click";
import PaginatedSearchableList from "../../selection-field/paginated-searchable-list";

import { Filter } from "../../../models/Filter";
import { Lookup } from "../../../models/Lookup";

import "./table-cell.css";

import { Direction } from "../../../constants/Direction";
import { DEF_PG_SIZE_FILTER } from "../../../constants/maximum-page-size";
import { useLocale } from "../../../contexts/localeContext";
import { useFilter } from "../../../contexts/table/FilterContext";
import { PaginationContext } from "../../../contexts/table/PaginationContext";
import { useSort } from "../../../contexts/table/SortContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import filterIcon from "../../../images/Filter.svg";
import columnSelectorIcon from "../../../images/Selector.svg";
import { PaginationModel } from "../../../models/Pagination";
import { TableColumn } from "../../../models/table/TableColumn";
import { buildSearchTerm } from "../../../utilities/SearchDescriptors";
import { toast } from "react-toastify";

type Props = {
    head?: boolean;
    borderOptions?: string;
    direction?: string;
    onSort?: (id: any, direction: string) => void;
    searchOnName?: string;
    customise?: boolean;
    onCustomise?: (event) => void;
    columnId?: string;
    children?: any;
    columnData?: Lookup[];
    isMultiSelect?: boolean;
    columnFilters?: Filter[];
    alignmentOptions?: string;
    formattingOptions?: string;
    handleFilterSelection?: any;
    columnAttributes?: any;
};

const TableCell: FC<Props> = memo(
    ({
        head = false,
        borderOptions = "border",
        direction = undefined,
        onSort = () => {},
        searchOnName = undefined,
        customise = false,
        onCustomise = () => {},
        columnId,
        children,
        columnData = [],
        isMultiSelect = false,
        columnFilters = undefined,
        alignmentOptions = "px-3 py-1",
        formattingOptions = "",
        columnAttributes,
    }) => {
        const pageSize = DEF_PG_SIZE_FILTER;
        const [searchHovered, setSearchHovered] = useState(false);
        const [customHovered, setCustomHovered] = useState(false);
        const [sortByDirection, setSortByDirection] = useState(Direction.ascending);
        const [searchBoxOpen, setSearchBoxOpen] = useState(false);
        const [columns, setColumns] = useState<any[]>([]);
        const [cellPagination, setCellPagination] = useState<PaginationModel>({
            totalCount: 0,
            maxPageSize: pageSize,
            totalPages: 1,
            pageNumber: 1,
        });
        const [selectedItems, setSelectedItems] = useState<any[]>([]);
        const [lookup, setLookup] = useState<Lookup | undefined>(columnData.find((lookup) => searchOnName?.startsWith(lookup.name)));
        const [searchTerm, setSearchTerm] = useState("");
        const [isFilterClicked, setIsFilterClicked] = useState(false);
        const filterContext = useFilter();
        const [isLoading, setIsLoading] = useState(false);
        const prevSearchTerm = useRef(searchTerm);
        const { translate } = useLocale();
        const prevPagination = useRef(cellPagination);

        useEffect(() => {
            if (!!searchOnName) {
                setLookup(columnData.find((lookup) => searchOnName?.startsWith(lookup.name)));
            }
        }, [columnData.length, searchOnName]);

        const fetchData = useCallback(async () => {
            if (isLoading) {
                return;
            }

            setIsLoading(true);
            try {
                const newSearchTerm = lookup?.searchAccessor && searchTerm ? buildSearchTerm(lookup.searchAccessor, searchTerm) : undefined;

                const { rows, pagination } = lookup?.dataMethod
                    ? await lookup.dataMethod({
                          goToPage: cellPagination.pageNumber,
                          pageSize: cellPagination.maxPageSize,
                          searchTerm: newSearchTerm,
                      })
                    : {
                          rows: lookup?.values,
                          pagination: {
                              ...cellPagination,
                              totalCount: lookup?.values.length ?? 0,
                              totalPages: Math.ceil((lookup?.values.length ?? 1) / pageSize),
                          },
                      };

                if (rows) {
                    setColumns(rows);
                    setCellPagination(pagination);
                    const newSelectedItems = mapOptionsToSelectedIndexes(columnFilters, rows);
                    setSelectedItems(newSelectedItems ?? []);
                }
            } catch (error) {
                toast.warn(translate.byKey(TranslationKeys.loadFilterFailed));
            } finally {
                setIsLoading(false);
                setIsFilterClicked(false);
            }
        }, [lookup, searchTerm, cellPagination, columnFilters, pageSize, translate]);

        useEffect(() => {
            if (isFilterClicked || cellPagination.pageNumber !== prevPagination.current.pageNumber|| searchTerm !== prevSearchTerm.current) {
                fetchData();
            }
            prevPagination.current = cellPagination;
            prevSearchTerm.current = searchTerm;
        }, [fetchData, isFilterClicked, cellPagination]);

        useEffect(() => {
            if (filterContext.isInUse) {
                const newSelectedItems = mapOptionsToSelectedIndexes(filterContext.filters, columns);
                setSelectedItems(newSelectedItems);
            }
        }, [searchBoxOpen, filterContext.isInUse, filterContext.filters, columns]);

        const handleFilterClick = () => {
            setIsFilterClicked(true);
            setSearchBoxOpen((prev) => !prev);
        };

        const getSortByDirection = (currentDir) => {
            const newDirection = !currentDir || currentDir === Direction.descending ? Direction.ascending : Direction.descending;
            setSortByDirection(newDirection);
            return newDirection;
        };

        const mapOptionsToSelectedIndexes = (filters, newColumnOptions) => {
            const currentFilter = filters?.find((filter) => filter.key === searchOnName);
            return newColumnOptions.map((option) => currentFilter?.selected?.some((item) => item.value === option.value) ? option.value : -1).filter((index) => index >= 0);
        };

        const generateSortByColumnId = (columnName: string): string => {
            let id = columnName.replaceAll(" ", "");
            id = id.replace(id[0], id[0].toLowerCase());
            return id + "SortById";
        };

        const generateSortByColumnSearchId = (columnName: string): string => {
            let id = columnName.replaceAll(" ", "");
            id = id.replace(id[0], id[0].toLowerCase());
            return id + "SearchById";
        };

        const handleSearch = (newSearchTerm: string) => {
            setSearchTerm(newSearchTerm);
            setCellPagination((prev) => ({ ...prev, pageNumber: 1 }));
        };

        const [sortIcon, setSortIcon] = useState(faChevronDown);
        const sortContext = useSort();
        const [columnDescriptor, setColumnDescriptor] = useState<TableColumn>({
            accessor: "",
            active: false,
            heading: "",
            id: 0,
            isNumeric: false,
            translationKey: "",
        });

        const handleSorting = () => {
            if (sortContext?.isInUse) {
                setSortIcon(columnDescriptor.sortDirection === Direction.descending ? faChevronDown : faChevronUp);
                sortContext.updateDirection(columnId);
            } else {
                onSort(columnId, getSortByDirection(sortByDirection));
            }
        };

        useEffect(() => {
            const newColumnDescriptor = sortContext.tableDescriptor.find((column) => column.id + "" === columnId + "");
            if (newColumnDescriptor) {
                setColumnDescriptor(newColumnDescriptor);
            }
        }, [columnId, sortContext.tableDescriptor]);

        const handleSelect = (index: number, value: any) => {
            const selectedItem = columns.find((item) => item.value === value);
            if (columnId !== undefined && columnId !== "") {
                filterContext.updateFilters(
                    columnId,
                    value,
                    selectedItem.label,
                );
            }
        };

        const updatePageNumber = (pageNumber: number) => {
            setCellPagination((prev) => ({ ...prev, pageNumber }));
        };

        if (head) {
            return (
                <>
                    {columnAttributes?.active && (
                        <th className={`${alignmentOptions} text-xs text-left font-normal ${borderOptions} whitespace-no-wrap`}>
                            <div className="flex justify-between">
                                <span className={customise ? "pt-px" : ""} id="tableHeaderId">
                                    {children}
                                    {sortContext.isInUse && columnDescriptor.sortDirection !== undefined ? (
                                        <FontAwesomeIcon
                                            className="mx-2 cursor-pointer"
                                            icon={sortIcon}
                                            onClick={handleSorting}
                                            id={generateSortByColumnId(children)}
                                        />
                                    ) : (
                                        !!direction && !!sortByDirection && (
                                            <FontAwesomeIcon
                                                className="mx-2 cursor-pointer"
                                                icon={sortByDirection === Direction.ascending ? faChevronDown : faChevronUp }
                                                onClick={handleSorting}
                                                id={generateSortByColumnId(children)}
                                            />
                                        )
                                    )}
                                </span>
                                <div className="flex flex-row">
                                    {!!searchOnName && !columnDescriptor.hideLookup && (
                                            <button
                                                type="button"
                                                className="no-select border-none bg-transparent outline-none pr-2"
                                                onClick={handleFilterClick}
                                                onMouseEnter={() => setSearchHovered(true)}
                                                onMouseLeave={() => setSearchHovered(false)}>
                                                <img
                                                    className="search-column-icon"
                                                    src={filterIcon}
                                                    alt=""
                                                    id={generateSortByColumnSearchId(children)}
                                                />
                                            </button>
                                        )}
                                    {customise && (
                                        <button
                                            type="button"
                                            className="no-select border-none bg-transparent outline-none px-1 interaction-container -mt-px -mb-1 -mx-2"
                                            id="columnSelector"
                                            onClick={onCustomise}
                                            onMouseEnter={() => setCustomHovered(true)}
                                            onMouseLeave={() => setCustomHovered(false)}>
                                            <img
                                                className="customise-column-icon"
                                                src={columnSelectorIcon}
                                                alt=""
                                            />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <PaginationContext.Provider
                                value={{
                                    isInUse: true,
                                    pagination: cellPagination,
                                    updatePageNumber,
                                    updatePageSize: () => {},
                                }}>
                                <OutsideClick onClick={() => setSearchBoxOpen(false)}>
                                    <div className={`relative mx-1 ${searchBoxOpen ? "" : "hidden"}`}>
                                        <div className="fixed bg-white shadow-md -mr-3 rounded-md px-2 py-2 z-50" id="searchColumnFilterModel">
                                            <PaginatedSearchableList
                                                items={columns}
                                                onSelect={handleSelect}
                                                isMultiSelect={isMultiSelect}
                                                selectedItems={selectedItems}
                                                onSearch={handleSearch}
                                                searchable={!!lookup?.searchAccessor}
                                            />
                                        </div>
                                    </div>
                                </OutsideClick>
                            </PaginationContext.Provider>
                        </th>
                    )}
                </>
            );
        } else {
            return (
                <>
                    {columnAttributes?.active && (
                        <td className={`${alignmentOptions} text-xs text-left ${borderOptions} ${formattingOptions}`}>
                            {children}
                        </td>
                    )}
                </>
            );
        }
    },
);

export default TableCell;

